import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { isNativeApp } from './native';
import { appPathFromLink, opensInApp } from './native-links';

/**
 * Wat een WebView niet vanzelf doet zoals een browser (MOB-2).
 *
 * De app laadt dezelfde schermen als de browser (ADR-0083). Drie dingen breken daar zonder deze
 * laag, en ze breken stil: een knop die niets doet.
 *
 * 1. **`window.open`.** Bijlagen, documenten en snelkoppelingen openen een nieuw tabblad. Een
 *    WebView heeft geen tabbladen. De vervanger opent de in-app browser, en kent het patroon van
 *    de app: eerst een leeg venster, daarna `location.href` zetten (zie `app-shortcuts.tsx`).
 * 2. **Links naar buiten.** Een link in een bericht naar een andere site hoort niet in het venster
 *    van de app te openen.
 * 3. **Een download** (`saveBlob`). Een WebView slaat een blob-link niet op. In de app gaat het
 *    bestand naar de cache en daarna naar het deelscherm van het toestel: opslaan, openen in een
 *    pdf-viewer, doorsturen.
 *
 * Plus de terugknop van Android en links van buiten (deeplinks) die de app openen.
 */

/** Opent een URL: de router voor een scherm van de app, anders de in-app browser. */
export function openOutside(url: string, navigate: (path: string) => void): void {
  const path = appPathFromLink(url, window.location.origin);
  if (path) {
    navigate(path);
    return;
  }
  void Browser.open({ url: new URL(url, window.location.origin).toString() });
}

/**
 * Een vervanger voor het venster dat `window.open` teruggeeft.
 *
 * Genoeg om de bestaande aanroepen te laten werken: `location.href` zetten opent de URL, `opener`
 * mag gezet worden, `close()` doet niets. Een echt venster bestaat er in een WebView niet.
 */
function fakeWindow(navigate: (path: string) => void): Window {
  const location = {
    set href(url: string) {
      openOutside(url, navigate);
    },
    get href() {
      return 'about:blank';
    },
    assign(url: string) {
      openOutside(url, navigate);
    },
    replace(url: string) {
      openOutside(url, navigate);
    },
  };
  return { location, opener: null, close: () => undefined } as unknown as Window;
}

/** Slaat een bestand op via het deelscherm van het toestel. */
export async function saveBlobNative(blob: Blob, filename: string): Promise<void> {
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error ?? new Error('Lezen mislukt'));
    reader.readAsDataURL(blob);
  });
  // De naam ontdoen van wat geen bestandsnaam kan zijn, zonder het herkenbare deel te verliezen.
  const safe = filename.replace(/[\\/:*?"<>|]+/g, '-').slice(0, 120) || 'bestand';
  const { uri } = await Filesystem.writeFile({ path: safe, data, directory: Directory.Cache });
  await Share.share({ title: safe, files: [uri] });
}

/**
 * Hangt de native laag aan de app. Eén keer, bij het starten; doet niets in een browser.
 *
 * `navigate` is de router van de app. `isHome` zegt of de gebruiker op het beginscherm staat: daar
 * sluit de terugknop de app, maar pas bij een tweede druk (`onExitHint` toont de uitleg).
 */
export function installNativeShell(options: {
  navigate: (path: string) => void;
  goBack: () => void;
  isHome: () => boolean;
  onExitHint: () => void;
}): () => void {
  if (!isNativeApp()) return () => undefined;
  const { navigate, goBack, isHome, onExitHint } = options;

  const originalOpen = window.open.bind(window);
  window.open = ((url?: string | URL) => {
    const target = url ? String(url) : '';
    // Leeg of `about:blank`: de aanroeper zet de bestemming daarna zelf (eerst openen, dan wachten).
    if (target && target !== 'about:blank') openOutside(target, navigate);
    return fakeWindow(navigate);
  }) as typeof window.open;

  const onClick = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0) return;
    const anchor = (event.target as Element | null)?.closest?.('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    const href = anchor.getAttribute('href') ?? '';
    if (!/^https?:/i.test(anchor.href) || href.startsWith('#')) return;
    // Een link binnen de app laat de router afhandelen; al de rest gaat naar de in-app browser.
    if (opensInApp(anchor.href, window.location.origin) && anchor.target !== '_blank') return;
    event.preventDefault();
    openOutside(anchor.href, navigate);
  };
  document.addEventListener('click', onClick, true);

  let lastBack = 0;
  const back = App.addListener('backButton', ({ canGoBack }) => {
    if (!isHome() && canGoBack) {
      goBack();
      return;
    }
    const now = Date.now();
    if (now - lastBack < 2000) {
      void App.exitApp();
      return;
    }
    lastBack = now;
    onExitHint();
  });

  // Een link van buiten (een mail, een QR-code) die de app opent: naar dat scherm.
  const opened = App.addListener('appUrlOpen', ({ url }) => {
    const path = appPathFromLink(url, window.location.origin);
    if (path) navigate(path);
  });

  return () => {
    window.open = originalOpen;
    document.removeEventListener('click', onClick, true);
    void back.then((handle) => handle.remove());
    void opened.then((handle) => handle.remove());
  };
}
